import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { PizzaEvaluationLayer } from "@/api/custom-schema";
import { EvaluationLayer } from "@/api/pizza-checker/schemas";
import { usePizzaEvaluationContext } from "@/context/pizza-evaluation-context";
import { useAsset } from "@/hooks/use-asset";
import { cn } from "@/lib/utils";

export type Layer = {
  name: string;
};

export type BlendingMode =
  | "normal"
  | "multiply"
  | "screen"
  | "overlay"
  | "darken"
  | "lighten"
  | "color-dodge"
  | "color-burn"
  | "hard-light"
  | "soft-light"
  | "difference"
  | "exclusion"
  | "hue"
  | "saturation"
  | "color"
  | "luminosity";

type LayerRendererProps = React.HTMLAttributes<HTMLCanvasElement> & {
  layer: PizzaEvaluationLayer | EvaluationLayer;
  // name: string;
};

const LayerRenderer = React.forwardRef<HTMLCanvasElement, LayerRendererProps>(
  ({ layer, className, ...props }, ref) => {
    const {
      pizzaImage,
      hoveredQuartal,
      selectedPizzaId,
      paddedPizzaRoi,
      enabledLayers,
      layerOpacity,
      layerZIndex,
      layerBlendMode,
    } = usePizzaEvaluationContext();

    const { name } = layer;
    const enabled =
      layer.type == "mask"
        ? (!hoveredQuartal && enabledLayers[name]) ||
          (!!hoveredQuartal && name.includes(hoveredQuartal))
        : enabledLayers[name];
    const opacity = layerOpacity[name] ?? 100;

    const { src } = useAsset({
      pizzaId: selectedPizzaId as string,
      type: layer.name.replace(" ", "_"),
      enabled: enabled && layer.type !== "source",
    });

    const previousSelectedPizzaId = useRef<string | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const [image, setImage] = useState<HTMLImageElement | null>(null);

    // Combine the forwarded ref with the internal ref
    const combinedRef = (node: HTMLCanvasElement | null) => {
      canvasRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };
    const imageToDraw = useMemo(() => {
      if (layer.type === "source") {
        return pizzaImage;
      }
      return image;
    }, [layer.type, pizzaImage, image]);

    useEffect(() => {
      if (!src) {
        setImage(null);
        return;
      }
      const img = new Image();
      img.src = src;
      previousSelectedPizzaId.current = selectedPizzaId;
      img.onload = () => setImage(img);
    }, [src]);

    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) {
        return;
      }

      if (!paddedPizzaRoi) {
        return;
      }

      if (previousSelectedPizzaId.current !== selectedPizzaId) {
        previousSelectedPizzaId.current = selectedPizzaId;

        // // Clear the canvas when the selected pizza changes
        // const ctx = canvas.getContext("2d");
        // if (ctx) {
        //   console.log(
        //     `Clearing canvas for layer ${name} with size ${canvas.width}x${canvas.height}`,
        //   );
        //   canvas.width = paddedPizzaRoi.width;
        //   canvas.height = paddedPizzaRoi.height;
        //   ctx.clearRect(0, 0, canvas.width, canvas.height);
        //   return;
        // }
      }

      if (!imageToDraw) {
        return;
      }

      canvas.width = paddedPizzaRoi.width;
      canvas.height = paddedPizzaRoi.height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        // ctx.shadowColor = "rgba(0, 0, 0, 1)";
        // ctx.shadowBlur = 50;
        // ctx.shadowOffsetX = 0;
        // ctx.shadowOffsetY = 0;

        // if (layer.type === "mask") {
        ctx.drawImage(
          imageToDraw,
          paddedPizzaRoi.x,
          paddedPizzaRoi.y,
          paddedPizzaRoi.width,
          paddedPizzaRoi.height, // source rect
          0,
          0,
          paddedPizzaRoi.width,
          paddedPizzaRoi.height, // dest rect
        );
        // setCroppedSrc(canvas.toDataURL());
      }
      // }
    }, [enabled, imageToDraw, paddedPizzaRoi, layer.name]);

    const zIndex = layerZIndex[name] ?? 0;
    const blendMode = layerBlendMode?.[name] ?? "normal";

    const [trueOpacity, setTrueOpacity] = useState(0);
    const [trueEnabled, setTrueEnabled] = useState(enabled);

    // useLayoutEffect(() => {
    //   if (enabled) setTrueOpacity(opacity);
    // }, [enabled, opacity]);

    useLayoutEffect(() => {
      let timeout: NodeJS.Timeout | undefined;
      if (enabled) {
        // setTrueOpacity(opacity);
        setTrueEnabled(true);
      } else {
        setTrueOpacity(0);
        timeout = setTimeout(() => setTrueEnabled(false), 1000); // adjust delay as needed
      }
      return () => {
        setTrueOpacity(opacity);
        if (timeout) clearTimeout(timeout);
      };
    }, [enabled, opacity]);

    useLayoutEffect(() => {
      if (trueEnabled) {
        setTrueOpacity(opacity);
      }
    }, [trueEnabled, opacity]);

    return (
      (enabled || trueEnabled) && (
        <canvas
          ref={combinedRef}
          {...props}
          className={cn("transition-all h-full duration-500", className)}
          style={{
            height: "calc-size(var(--intrinsic-size), size)",
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            filter: layer.type == "mask" ? "invert(100%)" : "none",
            // height: "100%",
            zIndex: zIndex,
            // opacity: opacity,
            opacity: trueOpacity / 100,
            mixBlendMode: (layer.type == "mask"
              ? "screen"
              : blendMode) as BlendingMode,
          }}
        />
      )
    );
  },
);

export default LayerRenderer;
